/**
 * CopilotModeTabs - Tabs for switching between Fusion Copilot conversations and Campaign Chat
 *
 * Shows when a campaign context is available (user is on a campaign page)
 * Allows easy switching between:
 * - Multiple Fusion Copilot tabs (Fusion Copilot, Fusion Copilot 2, etc.)
 * - Campaign Chat (campaign-specific assistance) - only ONE per campaign
 */

import React from 'react';
import { Box, Flex, Text, Badge, IconButton, Tooltip } from '@chakra-ui/react';
import { ChatIcon, CloseIcon } from '@chakra-ui/icons';
import { CampaignModeContext, FusionConversationTab } from '../context/CopilotChatContext';

interface CopilotModeTabsProps {
  /** Whether currently in campaign mode */
  isCampaignMode: boolean;
  /** Available campaign context (from the page) */
  availableCampaignContext: CampaignModeContext | null;
  /** Callback to enter campaign mode */
  onEnterCampaignMode: () => void;
  /** Callback to exit campaign mode (go to default copilot) */
  onExitCampaignMode: () => void;
  /** Fusion conversation tabs */
  fusionConversations?: FusionConversationTab[];
  /** Active Fusion conversation index */
  activeFusionConversationIndex?: number;
  /** Callback to switch Fusion conversation */
  onSwitchFusionConversation?: (index: number) => void;
  /** Callback to delete a Fusion conversation */
  onDeleteFusionConversation?: (conversationId: string) => void;
}

const CopilotModeTabs: React.FC<CopilotModeTabsProps> = ({
  isCampaignMode,
  availableCampaignContext,
  onEnterCampaignMode,
  onExitCampaignMode,
  fusionConversations = [],
  activeFusionConversationIndex = 0,
  onSwitchFusionConversation,
  onDeleteFusionConversation,
}) => {
  // Debug logging
  console.log('[CopilotModeTabs] Render - isCampaignMode:', isCampaignMode, 'fusionConversations:', fusionConversations.length);

  // Check if we have Fusion conversations or a campaign context
  const hasFusionTabs = fusionConversations.length > 0;
  const hasMultipleFusionTabs = fusionConversations.length > 1;
  const hasCampaignContext = !!availableCampaignContext;

  // Show tabs if:
  // - We have multiple Fusion tabs OR
  // - We have campaign context (either in campaign mode or campaign page available)
  // Don't show if there's only one Fusion tab and no campaign context
  // Also show if we're in campaign mode even with no Fusion tabs
  const shouldShowTabs = hasMultipleFusionTabs || hasCampaignContext || isCampaignMode;

  if (!shouldShowTabs) {
    return null;
  }

  const handleDeleteFusionTab = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    if (onDeleteFusionConversation) {
      onDeleteFusionConversation(conversationId);
    }
  };

  return (
    <Box
      bg="gray.50"
      borderBottomWidth={1}
      borderColor="gray.200"
      px={2}
      py={1}
      overflowX="auto"
      css={{
        '&::-webkit-scrollbar': {
          height: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#CBD5E0',
          borderRadius: '4px',
        },
      }}
    >
      <Flex gap={1} minW="max-content">
        {/* Fusion Copilot Tabs */}
        {fusionConversations.map((conv, index) => {
          const isActive = !isCampaignMode && index === activeFusionConversationIndex;

          return (
            <Flex
              key={conv.conversationId}
              align="center"
              gap={1}
              px={2}
              py={1.5}
              borderRadius="md"
              cursor="pointer"
              bg={isActive ? 'white' : 'transparent'}
              borderWidth={isActive ? 1 : 0}
              borderColor={isActive ? 'gray.200' : 'transparent'}
              boxShadow={isActive ? 'sm' : 'none'}
              onClick={() => {
                if (isCampaignMode) {
                  onExitCampaignMode();
                }
                if (onSwitchFusionConversation) {
                  onSwitchFusionConversation(index);
                }
              }}
              transition="all 0.15s"
              _hover={{
                bg: isActive ? 'white' : 'gray.100',
              }}
              position="relative"
              role="group"
            >
              <ChatIcon boxSize={3} color={isActive ? 'blue.500' : 'gray.500'} />
              <Text
                fontSize="sm"
                fontWeight={isActive ? '600' : '400'}
                color={isActive ? 'gray.800' : 'gray.600'}
                noOfLines={1}
                maxW="120px"
              >
                {conv.title}
              </Text>
              {/* X button always visible */}
              <Tooltip label="Close chat" fontSize="xs">
                <IconButton
                  aria-label="Close chat"
                  icon={<CloseIcon boxSize={2} />}
                  size="xs"
                  variant="ghost"
                  colorScheme="gray"
                  minW="16px"
                  h="16px"
                  opacity={0.5}
                  _hover={{ opacity: 1, bg: 'red.100', color: 'red.500' }}
                  onClick={(e) => handleDeleteFusionTab(e, conv.conversationId)}
                  ml={1}
                />
              </Tooltip>
            </Flex>
          );
        })}

        {/* Campaign Chat Tab - only show if campaign context is available */}
        {hasCampaignContext && (
          <Flex
            align="center"
            gap={1.5}
            px={3}
            py={1.5}
            borderRadius="md"
            cursor="pointer"
            bg={isCampaignMode ? 'white' : 'transparent'}
            borderWidth={isCampaignMode ? 1 : 0}
            borderColor={isCampaignMode ? 'orange.200' : 'transparent'}
            boxShadow={isCampaignMode ? 'sm' : 'none'}
            onClick={onEnterCampaignMode}
            transition="all 0.15s"
            _hover={{
              bg: isCampaignMode ? 'white' : 'gray.100',
            }}
          >
            <Badge
              colorScheme="orange"
              variant="solid"
              fontSize="9px"
              px={1}
              borderRadius="sm"
            >
              AI
            </Badge>
            <Text
              fontSize="sm"
              fontWeight={isCampaignMode ? '600' : '400'}
              color={isCampaignMode ? 'orange.600' : 'gray.600'}
              noOfLines={1}
              maxW="150px"
            >
              {availableCampaignContext.campaignTitle || 'Campaign Chat'}
            </Text>
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default CopilotModeTabs;
